// Indicator Detail Panel - Full detail view for selected indicator
import React, { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Info,
  Download,
  Clock,
  Database,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { useIndicatorData, usePeerComparison } from '../../hooks/useIndicatorData';
import { getIndicatorById } from '../../data/indicatorRegistry';

interface IndicatorDetailPanelProps {
  indicatorId: string;
  countryCode?: string;
  onExport?: (indicatorId: string, format: 'csv' | 'png') => void;
}

type TimeRange = '5Y' | '10Y' | '20Y' | 'ALL';

// Central Asia & Silk Road Corridor peers
const PEER_COUNTRIES = ['UZB', 'KAZ', 'KGZ', 'TJK', 'TKM', 'AZE', 'GEO', 'MNG'];
const COUNTRY_NAMES: Record<string, string> = {
  UZB: 'Uzbekistan',
  KAZ: 'Kazakhstan',
  KGZ: 'Kyrgyzstan',
  TJK: 'Tajikistan',
  TKM: 'Turkmenistan',
  AZE: 'Azerbaijan',
  GEO: 'Georgia',
  MNG: 'Mongolia',
};

const COUNTRY_COLORS: Record<string, string> = {
  UZB: '#00d26a',  // Primary - Green
  KAZ: '#fbbf24',  // Amber
  KGZ: '#00a3ff',  // Blue
  TJK: '#00d4d4',  // Cyan
  TKM: '#f97316',  // Orange
  AZE: '#ef4444',  // Red
  GEO: '#a855f7',  // Purple
  MNG: '#6b7280',  // Gray
};

// Format value based on unit and scale
const formatValue = (value: number | null, unit: string, decimals: number = 2): string => {
  if (value === null || value === undefined) return 'N/A';

  if (unit === '%') return `${value.toFixed(decimals)}%`;
  if (unit === 'Index' || unit === 'Score') return value.toFixed(decimals);
  if (unit === 'Rating') return value.toFixed(0);

  if (unit.includes('USD') || unit.includes('$')) {
    if (Math.abs(value) >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    return `$${value.toFixed(0)}`;
  }

  if (unit.includes('millions')) {
    return `${value.toFixed(1)}M`;
  }

  if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;

  return value.toFixed(decimals);
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, unit }: {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
  unit?: string;
}) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-terminal-bg border border-terminal-border rounded px-2 py-1 shadow-lg">
      <p className="text-[10px] text-gray-400 mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-[11px]" style={{ color: entry.color }}>
          {entry.name}: {formatValue(entry.value, unit || '', 2)}
        </p>
      ))}
    </div>
  );
};

export const IndicatorDetailPanel: React.FC<IndicatorDetailPanelProps> = ({
  indicatorId,
  countryCode = 'UZB',
  onExport,
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('10Y');
  const [showPeerComparison, setShowPeerComparison] = useState(true);

  const metadata = useMemo(() => getIndicatorById(indicatorId), [indicatorId]);
  const { data, forecast, statistics, yoyChange, isLoading, source, lastUpdated } = useIndicatorData(
    indicatorId,
    countryCode,
    timeRange === '5Y' ? 5 : timeRange === '10Y' ? 10 : timeRange === '20Y' ? 20 : 30
  );
  const { latestValues: peerLatest } = usePeerComparison(
    indicatorId,
    PEER_COUNTRIES
  );

  // Combine historical and forecast data for chart
  const chartData = useMemo(() => {
    const combined = [...data];
    if (forecast && forecast.length > 0) {
      forecast.forEach((f: { year: number; value: number | null }) => {
        if (!combined.find(d => d.year === f.year)) {
          combined.push(f);
        }
      });
    }
    return combined.sort((a, b) => a.year - b.year);
  }, [data, forecast]);

  // Calculate statistics
  const latestValue = statistics?.latest ?? null;
  const latestChange = yoyChange.length > 0 ? yoyChange[yoyChange.length - 1]?.change : null;
  const isPositive = latestChange !== null && latestChange > 0;
  const goodDirection = metadata?.goodDirection ?? 'up';
  const isGood = goodDirection === 'up' ? isPositive : goodDirection === 'down' ? !isPositive : true;

  // Prepare peer comparison bar data
  const peerBarData = useMemo(() => {
    return PEER_COUNTRIES.map(code => ({
      country: COUNTRY_NAMES[code] || code,
      code,
      value: peerLatest[code]?.value ?? null,
      color: COUNTRY_COLORS[code] || '#6b7280',
    })).filter(d => d.value !== null).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }, [peerLatest]);

  if (!metadata) {
    return (
      <div className="flex items-center justify-center h-full bg-terminal-panel text-gray-500">
        <p>Select an indicator to view details</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-terminal-panel">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-data-amber" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-terminal-panel overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-terminal-border">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-white truncate">{metadata.name}</h2>
            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{metadata.description}</p>
          </div>

          {/* Current Value */}
          <div className="text-right shrink-0">
            <div className="text-2xl font-bold text-white">
              {formatValue(latestValue, metadata.unit)}
            </div>
            {latestChange !== null && (
              <div className={`flex items-center justify-end gap-1 mt-0.5 ${
                isGood ? 'text-data-green' : 'text-data-red'
              }`}>
                {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <span className="text-sm">
                  {latestChange > 0 ? '+' : ''}{latestChange.toFixed(1)}% YoY
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Meta Info Row */}
        <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-500">
          <div className="flex items-center gap-1">
            <Database size={10} />
            <span>{source === 'imf' ? 'IMF' : source === 'worldbank' ? 'World Bank' : 'NATEX'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={10} />
            <span>{metadata.frequency}</span>
          </div>
          {lastUpdated && (
            <div className="flex items-center gap-1">
              <span>Updated: {lastUpdated.toLocaleDateString()}</span>
            </div>
          )}
          <div className="flex-1" />
          {onExport && (
            <button
              onClick={() => onExport(indicatorId, 'csv')}
              className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
            >
              <Download size={10} />
              <span>Export</span>
            </button>
          )}
        </div>
      </div>

      {/* Content Area - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          {(['5Y', '10Y', '20Y', 'ALL'] as TimeRange[]).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-2 py-1 text-[10px] rounded transition-colors ${
                timeRange === range
                  ? 'bg-data-amber text-black'
                  : 'bg-terminal-bg text-gray-400 hover:text-white'
              }`}
            >
              {range}
            </button>
          ))}
        </div>

        {/* Main Chart */}
        <div className="h-64 bg-terminal-bg rounded border border-terminal-border p-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ffb800" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ffb800" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#374151' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#374151' }}
                tickFormatter={(v) => formatValue(v, metadata.unit, 0)}
              />
              <Tooltip content={<CustomTooltip unit={metadata.unit} />} />
              {statistics?.avg && (
                <ReferenceLine
                  y={statistics.avg}
                  stroke="#6b7280"
                  strokeDasharray="3 3"
                  label={{ value: 'Avg', fontSize: 9, fill: '#6b7280' }}
                />
              )}
              <Area
                type="monotone"
                dataKey="value"
                stroke="#ffb800"
                strokeWidth={2}
                fill="url(#valueGradient)"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.isForecast) {
                    return <circle cx={cx} cy={cy} r={3} fill="#ffb800" strokeDasharray="2 2" />;
                  }
                  return null;
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Latest', value: statistics?.latest, year: statistics?.latestYear },
            { label: 'Average', value: statistics?.avg },
            { label: 'Minimum', value: statistics?.min },
            { label: 'Maximum', value: statistics?.max },
          ].map((stat, i) => (
            <div key={i} className="bg-terminal-bg rounded p-2 border border-terminal-border">
              <div className="text-[9px] text-gray-500 uppercase">{stat.label}</div>
              <div className="text-sm font-semibold text-white mt-0.5">
                {stat.value !== null && stat.value !== undefined
                  ? formatValue(stat.value, metadata.unit)
                  : 'N/A'}
              </div>
              {stat.year && (
                <div className="text-[9px] text-gray-600">{stat.year}</div>
              )}
            </div>
          ))}
        </div>

        {/* Peer Comparison */}
        {showPeerComparison && peerBarData.length > 0 && (
          <div className="bg-terminal-bg rounded border border-terminal-border p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-medium text-gray-300 uppercase">Peer Comparison ({peerBarData.length} countries)</h3>
              <button
                onClick={() => setShowPeerComparison(false)}
                className="text-[9px] text-gray-500 hover:text-white"
              >
                Hide
              </button>
            </div>
            {/* Dynamic height: 28px per country + padding */}
            <div style={{ height: Math.max(160, peerBarData.length * 28 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={peerBarData} layout="vertical" barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 9, fill: '#9ca3af' }}
                    tickFormatter={(v) => formatValue(v, metadata.unit, 0)}
                  />
                  <YAxis
                    type="category"
                    dataKey="country"
                    tick={{ fontSize: 9, fill: '#9ca3af' }}
                    width={85}
                    interval={0}
                  />
                  <Tooltip content={<CustomTooltip unit={metadata.unit} />} />
                  <Bar
                    dataKey="value"
                    radius={[0, 4, 4, 0]}
                  >
                    {peerBarData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Methodology */}
        {metadata.methodology && (
          <div className="bg-terminal-bg rounded border border-terminal-border p-3">
            <div className="flex items-center gap-2 mb-2">
              <Info size={12} className="text-gray-500" />
              <h3 className="text-[11px] font-medium text-gray-300 uppercase">Methodology</h3>
            </div>
            <p className="text-[10px] text-gray-500 leading-relaxed">{metadata.methodology}</p>
          </div>
        )}

        {/* Thresholds */}
        {metadata.thresholds && (
          <div className="bg-terminal-bg rounded border border-terminal-border p-3">
            <h3 className="text-[11px] font-medium text-gray-300 uppercase mb-2">Reference Thresholds</h3>
            <div className="flex items-center gap-4 text-[10px]">
              <div>
                <span className="text-gray-500">Low: </span>
                <span className={latestValue !== null && latestValue < metadata.thresholds.low ? 'text-data-red' : 'text-gray-400'}>
                  {formatValue(metadata.thresholds.low, metadata.unit)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">High: </span>
                <span className={latestValue !== null && latestValue > metadata.thresholds.high ? 'text-data-green' : 'text-gray-400'}>
                  {formatValue(metadata.thresholds.high, metadata.unit)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {data.length > 0 && (
          <div className="bg-terminal-bg rounded border border-terminal-border p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-medium text-gray-300 uppercase">Historical Data</h3>
              <span className="text-[9px] text-gray-500">{data.length} data points</span>
            </div>
            <div className="max-h-48 overflow-y-auto scrollbar-thin">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 bg-terminal-bg">
                  <tr className="border-b border-terminal-border">
                    <th className="text-left py-1.5 text-gray-500 font-medium">Year</th>
                    <th className="text-right py-1.5 text-gray-500 font-medium">Value</th>
                    <th className="text-right py-1.5 text-gray-500 font-medium">YoY Change</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data].sort((a, b) => b.year - a.year).map((point, idx) => {
                    const prevYear = data.find(d => d.year === point.year - 1);
                    const yoyChange = prevYear && prevYear.value !== null && point.value !== null && prevYear.value !== 0
                      ? ((point.value - prevYear.value) / Math.abs(prevYear.value)) * 100
                      : null;
                    const isPositive = yoyChange !== null && yoyChange > 0;
                    const changeColor = yoyChange === null ? 'text-gray-600'
                      : (goodDirection === 'up' ? (isPositive ? 'text-data-green' : 'text-data-red')
                        : (isPositive ? 'text-data-red' : 'text-data-green'));

                    return (
                      <tr key={point.year} className="border-b border-terminal-border/30 hover:bg-terminal-panel/30">
                        <td className="py-1.5 text-gray-400">{point.year}</td>
                        <td className="py-1.5 text-right text-white font-mono">
                          {point.value !== null ? formatValue(point.value, metadata.unit) : 'N/A'}
                        </td>
                        <td className={`py-1.5 text-right font-mono ${changeColor}`}>
                          {yoyChange !== null ? `${yoyChange > 0 ? '+' : ''}${yoyChange.toFixed(1)}%` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Related Indicators */}
        {metadata.relatedIndicators && metadata.relatedIndicators.length > 0 && (
          <div className="bg-terminal-bg rounded border border-terminal-border p-3">
            <h3 className="text-[11px] font-medium text-gray-300 uppercase mb-2">Related Indicators</h3>
            <div className="flex flex-wrap gap-1">
              {metadata.relatedIndicators.map(id => {
                const related = getIndicatorById(id);
                return related ? (
                  <span
                    key={id}
                    className="px-2 py-0.5 bg-terminal-panel rounded text-[9px] text-gray-400"
                  >
                    {related.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IndicatorDetailPanel;
